// i18n ignore
import { projectSettingApi } from '@/services';
import { fetchProjectInfo } from '@/store/project/project';
import { useRouter } from '@/router/useRouter';
import { safeAsync } from './types';
/**
 * 生成机器人通知方法的类型定义
 */
function generateRobotMethodDts(robotList) {
    const robotIdType = robotList.map(r => `'${r.id}'`).join(' | ');
    const robotComment = robotList.map(r => `${r.id}: ${r.name}`).join('，');
    return `
    /**
     * 机器人通知（依赖应用配置）
     * @example
     * \`\`\`typescript
     * const wxwork = useWxwork();
     * await wxwork.sendRobotMessage({
     *   robotId: 'robot-id',
     *   message: { msgtype: 'text', text: { content: 'Hello' } }
     * });
     * \`\`\`
     */
    sendRobotMessage(params: {
      /** 机器人ID（${robotComment}） */
      robotId: ${robotIdType};
      /** 消息内容（JSON格式，参考企微机器人API） */
      message: string | {
        msgtype: 'text' | 'markdown' | 'image' | 'news';
        text?: { content: string };
        markdown?: { content: string };
        [key: string]: any;
      };
    }): Promise<void>;`;
}
/**
 * 生成应用消息通知方法的类型定义
 */
function generateAppMessageMethodDts(weworkApp) {
    const { hasBuiltinApp, hasCustomApp } = weworkApp;
    // 根据配置生成 weworkApp 的类型
    const weworkAppTypes = [];
    if (hasBuiltinApp)
        weworkAppTypes.push('\'builtin\'');
    if (hasCustomApp)
        weworkAppTypes.push('\'custom\'');
    const weworkAppType = weworkAppTypes.join(' | ') || 'string';
    // 生成 weworkApp 参数的注释
    const weworkAppComments = [];
    if (hasBuiltinApp)
        weworkAppComments.push('\'builtin\' 使用平台内置应用');
    if (hasCustomApp)
        weworkAppComments.push('\'custom\' 使用应用自定义配置');
    const weworkAppComment = weworkAppComments.join('，') || '通知应用';
    return `
    /**
     * 应用消息通知
     * @example
     * \`\`\`typescript
     * const wxwork = useWxwork();
     * await wxwork.sendAppMessage({
     *   receivers: 'user1,user2',
     *   type: 'text',
     *   content: '通知内容'
     * });
     * \`\`\`
     */
    sendAppMessage(params: {
      /** 接收人（多个用逗号/分号分隔） */
      receivers: string;
      /** 消息类型 */
      type: 'text' | 'markdown' | 'textcard';
      /** 消息内容（text/markdown类型使用） */
      content?: string;
      /** 标题（textcard类型使用） */
      title?: string;
      /** 描述（textcard类型使用） */
      description?: string;
      /** 链接（textcard类型使用） */
      url?: string;
      /** 按钮文本（textcard类型使用，默认"详情"） */
      buttonText?: string;
      /** 通知应用：${weworkAppComment} */
      weworkApp?: ${weworkAppType};
    }): Promise<void>;`;
}
/**
 * 生成客服号消息通知方法的类型定义
 */
function generateCustomerMessageMethodDts() {
    return `
    /**
     * 客服号消息通知
     * @description 通过平台配置的企微客服号发送一级会话消息，仅支持通知，不支持消息内审批
     * @example
     * \`\`\`typescript
     * const wxwork = useWxwork();
     * await wxwork.sendCustomerMessage({
     *   receivers: 'user1,user2',
     *   type: 'markdown',
     *   content: '### 通知标题\\n通知内容'
     * });
     * \`\`\`
     */
    sendCustomerMessage(params: {
      /** 接收人（多个用逗号/分号分隔） */
      receivers: string;
      /** 消息类型 */
      type: 'text' | 'markdown';
      /** 消息内容 */
      content: string;
    }): Promise<void>;`;
}
/**
 * 生成企业微信 useWxwork 的类型定义
 */
function generateWxworkDts(config) {
    const { robotList, weworkApp, hasCustomerNotification } = config;
    const hasRobotConfig = robotList.length > 0;
    const hasWeworkApp = weworkApp.hasBuiltinApp || weworkApp.hasCustomApp;
    // 如果没有任何企业微信配置，不注入
    if (!hasRobotConfig && !hasWeworkApp && !hasCustomerNotification)
        return '';
    const methods = [];
    if (hasRobotConfig) {
        methods.push(generateRobotMethodDts(robotList));
    }
    if (hasWeworkApp) {
        methods.push(generateAppMessageMethodDts(weworkApp));
    }
    if (hasCustomerNotification) {
        methods.push(generateCustomerMessageMethodDts());
    }
    return `
  /**
   * 企业微信相关功能
   */
  export function useWxwork(): {${methods.join('')}
  };`;
}
/**
 * 获取机器人配置列表
 */
async function fetchRobotList(projectId) {
    const robots = await projectSettingApi.fetchRobotList(projectId);
    return robots.map((r) => ({
        id: r.id,
        name: r.name,
    }));
}
/**
 * 获取企业微信应用配置
 */
async function fetchWeworkAppConfig(projectId) {
    const { route } = useRouter();
    const branchId = String(route.value?.query?.branch || route.value?.query?.branchid || '');
    const project = await fetchProjectInfo(projectId, branchId);
    const advanceConfig = project?.advanceConfig;
    // 检查是否有平台内置的企业微信应用
    const hasBuiltinApp = !!window.CONFIGS?.weworkAppName;
    // 检查是否有应用自定义的企业微信应用
    const hasCustomApp = !!(advanceConfig?.enabledWeworkApp && advanceConfig?.weworkAppConfig);
    return { hasBuiltinApp, hasCustomApp };
}
/**
 * 企业微信 DTS 注入器
 */
export const wxworkInjector = {
    placeholder: '/** inject-wxwork-dts */',
    async generate(projectId) {
        // 并行获取机器人配置和企业微信应用配置
        const [robotList, weworkApp] = await Promise.all([
            safeAsync(() => fetchRobotList(projectId), [], '获取项目设置失败'),
            safeAsync(() => fetchWeworkAppConfig(projectId), { hasBuiltinApp: false, hasCustomApp: false }, '获取项目配置失败'),
        ]);
        const hasCustomerNotification = (window.CONFIGS?.workflowNotificationPlugins || [])
            .some((plugin) => plugin.pluginName === 'work-wx-customer');
        return generateWxworkDts({ robotList, weworkApp, hasCustomerNotification });
    },
};
// 导出给单元测试使用
export { generateWxworkDts, generateRobotMethodDts, generateAppMessageMethodDts, generateCustomerMessageMethodDts };
